import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useSWR from "swr";
import urls from "../../api/urls"
import {api, HydraData} from "../../api/apiProvider";
import {Card, DatePicker, Flex, Form, message, Select, Space} from "antd";
import ButtonBlack from "../../components/ui/buttons/ButtonBlack";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import Title from "antd/es/typography/Title";
import TextArea from "antd/lib/input/TextArea";
import useSWRMutation from "swr/mutation";
import dayjs, {Dayjs} from "dayjs";
import type {DefaultOptionType} from "antd/es/select";
import {debounce} from "lodash";
import Stages from "../../components/brief/Stages";
import WorkGroup from "../../components/brief/WorkGroup";
import {Channel, Kpi, ModelType} from "../../api/types";
import Roles from "../../roles/Roles";
import getBriefParamsString from "../../api/params";
import {isEqualValues} from "../../utils";
import Forbidden from "../results/forbidden";

const {RangePicker} = DatePicker;

const dateFormat = 'YYYY-MM-DD';

interface Model {
  id: string,
  '@id': string,
  name: string,
}

interface BriefChannel {
  '@id': string,
  channel: Channel,
}

interface BriefStageType {
  stageType: string,
  dateEnd: string | null,
  isAccepted: boolean | null,
  id?: number,
  brief: string,
}

interface BriefType {
  name?: string,
  products: string[],
  efficiencyMetrics?: string[],
  kpi: string,
  modelType: string,
  comment: string,
  seasonalityText: string,
  kpiDescription: string,
  mainCompetitors: string,
  bht: string,
  audienceText: string,
  dataDateStart: string,
  dataDateEnd: string,
  specificText: string,
  requiresValidation: boolean,
  isPresentationRequired: boolean,
  isCalculatorRequired: boolean,
  stages?: BriefStageType[],
}

interface Model {
  label: string,
  value: string,
}

interface Value {
  audienceText: string,
  bht: undefined,
  company: Model,
  efficiencyMetrics: Model[],
  kpi: Model,
  modelType: Model,
  period: [Dayjs, Dayjs],
  pool: Model,
  products: Model[],
  seasonalityText: string,
  specificText: string,
}

export default function Description() {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [companySearch, setCompanySearch] = useState<string>('');
  const [poolSearch, setPoolSearch] = useState<string>('');
  const [productSearch, setProductSearch] = useState<string>('');
  const [efficiencyMetricsSearch, setEfficiencyMetricsSearch] = useState<string>('');
  const [selectedPoolId, setSelectedPoolId] = useState<string>('');
  const [selectedModelType, setSelectedModelType] = useState<ModelType>();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companiesOptions, setCompaniesOptions] = useState<DefaultOptionType[]>([]);
  const [productsOptions, setProductsOptions] = useState<DefaultOptionType[]>([]);
  const [kpiOptions, setKpiOptions] = useState<DefaultOptionType[]>([]);
  const [modelTypesOptions, setModelTypesOptions] = useState<DefaultOptionType[]>([]);
  const [poolsOptions, setPoolsOptions] = useState<DefaultOptionType[]>([]);
  const [efficiencyMetricsOptions, setEfficiencyMetricsOptions] = useState<DefaultOptionType[]>([]);
  const [initialValues, setInitialValues] = useState<Value>();
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false);

  const routeParams = useParams();
  const [searchParams] = useSearchParams();
  const briefId = Number(routeParams.id) || 0;
  const briefIdForCopy = Number(searchParams.get('id'));

  const [briefForm] = Form.useForm();

  const {
    data: brief,
    error: briefError,
    mutate: mutateBrief,
    isValidating: isBriefValidating,
  } = useSWR<HydraData>((briefId > 0 || briefIdForCopy > 0) ? `${urls.briefs}/${(briefId || briefIdForCopy)}?${getBriefParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  });

  const getKpisParamsString = () => {
    const params = new URLSearchParams([
      ['pagination', 'false']
    ]);
    return params.toString()
  };

  const {
    data: kpis,
    error: kpisError,
    mutate: mutateKpis,
    isValidating: isKpisValidating,
  } = useSWR<HydraData>(`${urls.kpis}?${getKpisParamsString()}`, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: pools,
    error: poolsError,
  } = useSWR<HydraData>(poolSearch ? `${urls.pools}?name=${poolSearch}` : `${urls.pools}`, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: efficiencyMetrics,
    error: efficiencyMetricsError,
  } = useSWR<HydraData>(efficiencyMetricsSearch
    ? `${urls.efficiencyMetrics}?name=${efficiencyMetricsSearch}` : `${urls.efficiencyMetrics}`, api.get, {
    revalidateOnFocus: false
  });

  const getCompaniesParamsString = () => {
    const params = new URLSearchParams([
      ['pagination', 'false']
    ]);

    if (companySearch) {
      params.append('name', companySearch)
    }

    if (selectedPoolId) {
      params.append('pool.id', selectedPoolId)
    }

    return params.toString()
  };

  const {
    data: companies,
  } = useSWR<HydraData>(`${urls.companies}?${getCompaniesParamsString()}`, api.get, {
    revalidateOnFocus: false
  });

  const getProductsParamsString = () => {
    const params = new URLSearchParams();

    if (productSearch) {
      params.append('name', productSearch)
    }

    if (selectedCompanyId) {
      params.append('company.id', selectedCompanyId)
    }

    return params.toString()
  };

  const {
    data: products,
    //isValidating: isPoolsValidating
  } = useSWR<HydraData>(`${urls.products}?${getProductsParamsString()}`, api.get, {
    revalidateOnFocus: false
  });

  const {
    data: modelTypes,
    //isValidating: isModelTypesValidating
  } = useSWR<HydraData>(`${urls.modelTypes}`, api.get, {
    revalidateOnFocus: false
  });

  const {trigger: createBrief, isMutating: isBriefCreating}
    = useSWRMutation(urls.briefs, api.post, /* options */);

  const {trigger: updateBrief, isMutating: isBriefUpdating}
    = useSWRMutation(`${urls.briefs}/${briefId}`, api.patch, /* options */);

  useEffect(() => {
    if (brief && brief.data) {
      briefForm.setFieldValue('seasonalityText', brief.data.seasonalityText);
      briefForm.setFieldValue('mainCompetitors', brief.data.mainCompetitors);
      briefForm.setFieldValue('kpiDescription', brief.data.kpiDescription);
      briefForm.setFieldValue('specificText', brief.data.specificText);
      briefForm.setFieldValue('audienceText', brief.data.audienceText);
      briefForm.setFieldValue('requiresValidation', brief.data.requiresValidation);
      briefForm.setFieldValue('isPresentationRequired', brief.data.isPresentationRequired);
      briefForm.setFieldValue('isCalculatorRequired', brief.data.isCalculatorRequired);
      briefForm.setFieldValue('bht', brief.data.bht);
      briefForm.setFieldValue('period', [dayjs(brief.data.dataDateStart), dayjs(brief.data.dataDateEnd)]);

      if (brief.data.products && brief.data.products.length > 0) {
        const products = brief.data.modelType && brief.data.modelType.isBriefManyProducts
          ? brief.data.products.map((product: Model) => ({
            value: product['@id'],
            label: product.name,
          }))
          : {
            label: brief.data.products[0].name,
            value: brief.data.products[0]['@id'],
          };
        briefForm.setFieldValue('products', products);

        briefForm.setFieldValue('company', {
          value: brief.data.products[0].company.id,
          label: brief.data.products[0].company.name,
        });

        briefForm.setFieldValue('pool', {
          value: brief.data.products[0].company.pool.id,
          label: brief.data.products[0].company.pool.name,
        });

        setSelectedCompanyId(brief.data.products[0].company.id)
      }

      if (briefId > 0 && brief.data.kpi && brief.data.kpi['@id']) {
        briefForm.setFieldValue('kpi', {
          value: brief.data.kpi['@id'],
          label: brief.data.kpi.name,
        })
      }

      if (brief.data.modelType && brief.data.modelType['@id']) {
        briefForm.setFieldValue('modelType', {
          value: brief.data.modelType['@id'],
          label: brief.data.modelType.name,
        });
        setSelectedModelType(brief.data.modelType)
      }

      if (brief.data.efficiencyMetrics && brief.data.efficiencyMetrics.length > 0) {
        briefForm.setFieldValue('efficiencyMetrics', brief.data.efficiencyMetrics.map((efficiencyMetric: Model) => ({
          value: efficiencyMetric['@id'],
          label: efficiencyMetric.name,
        })))
      }

      setInitialValues(briefForm.getFieldsValue())
    }
  }, [brief]);

  useEffect(() => {
    if (companies && companies.data) {
      setCompaniesOptions(companies.data.map((company: Model) => {
        return {
          value: company.id,
          label: company.name,
        }
      }))
    }
  }, [companies]);

  useEffect(() => {
    if (pools && pools.data) {
      setPoolsOptions(pools.data.map((pool: Model) => {
        return {
          value: pool.id,
          label: pool.name,
        }
      }))
    }
  }, [pools]);

  useEffect(() => {
    if (products && products.data) {
      setProductsOptions(products.data.map((product: Model) => {
        return {
          value: product['@id'],
          label: product.name,
        }
      }))
    }
  }, [products]);

  useEffect(() => {
    if (kpis && kpis.data) {
      setKpiOptions(kpis.data.map((kpi: Model) => {
        return {
          value: kpi['@id'],
          label: kpi.name,
        }
      }))
    }
  }, [kpis]);

  useEffect(() => {
    if (modelTypes && modelTypes.data) {
      setModelTypesOptions(modelTypes.data.map((modelType: Model) => {
        return {
          value: modelType['@id'],
          label: modelType.name,
        }
      }))
    }
  }, [modelTypes]);

  useEffect(() => {
    if (efficiencyMetrics && efficiencyMetrics.data) {
      setEfficiencyMetricsOptions(efficiencyMetrics.data.map((efficiencyMetric: Model) => {
        return {
          value: efficiencyMetric['@id'],
          label: efficiencyMetric.name,
        }
      }))
    }
  }, [efficiencyMetrics]);

  const onFinish = (values: any) => {
    if (!Roles.hasAccess('canCreateOrUpdateBrief')) {
      return
    }

    const brief: BriefType = {
      audienceText: values.audienceText,
      bht: values.bht,
      comment: values.comment,
      dataDateEnd: values.period[1] ? values.period[1].format(dateFormat) : null,
      dataDateStart: values.period[0] ? values.period[0].format(dateFormat) : null,
      kpi: values.kpi ? values.kpi.value : null,
      modelType: values.modelType ? values.modelType.value : null,
      products: Array.isArray(values.products)
        ? values.products.map((product: DefaultOptionType) => product.value)
        : [values.products.value],
      seasonalityText: values.seasonalityText,
      specificText: values.specificText,
      mainCompetitors: values.mainCompetitors,
      kpiDescription: values.kpiDescription,
      requiresValidation: values.requiresValidation,
      isPresentationRequired: values.isPresentationRequired,
      isCalculatorRequired: values.isCalculatorRequired,
      efficiencyMetrics: values.efficiencyMetrics.map((efficiencyMetric: DefaultOptionType) => efficiencyMetric.value),
    };

    if (briefId > 0) {
      updateBrief(brief).then(() => {
        message.success(t('common.saved'));
        mutateBrief();
        setIsValuesChanged(false)
      }).catch(errors => {
        if (errors['hydra:description']) {
          message.error(errors['hydra:description'])
        }
        setIsValuesChanged(false)
      })
    } else {
      createBrief(brief).then((response) => {
        if (response && response.id) {
          message.success(t('common.saved'))
        }
        setIsValuesChanged(false);
        navigate(`/briefs/${response.id}/description`)
      }).catch(errors => {
        if (errors['hydra:description']) {
          message.error(errors['hydra:description'])
        }
        setIsValuesChanged(false)
      })
    }
  };

  const isBriefManyProducts = () => {
    if (!selectedModelType) {
      return false
    }

    return selectedModelType.isBriefManyProducts
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    for (let key in initialValues) {
      if (!initialValues.hasOwnProperty(key)) {
        continue
      }

      const initialValue = initialValues[key as keyof Value];
      if (key in allValues) {
        const changedValue = allValues[key];

        if (!isEqualValues(initialValue, changedValue)) {
          setIsValuesChanged(true);
          return
        }
      }
    }

    setIsValuesChanged(false)
  };

  return (
    <>
      {Roles.hasAccess('canReadBrief')
        ? <Flex gap={15} justify={'center'}>
          <Flex gap={'large'} style={{width: '706px', maxWidth: '706px'}} vertical>
            <Card>
              <Space direction={'vertical'} size={'middle'} style={{width: '100%'}}>
                <Title level={5}>{t('edit.fillDescription')}</Title>

                <Form
                  form={briefForm}
                  layout="vertical"
                  name="brief"
                  onFinish={onFinish}
                  disabled={!Roles.hasAccess('canCreateOrUpdateBrief')}
                  onValuesChange={onValuesChange}
                  initialValues={initialValues}
                >
                  <div style={{display: 'block'}}>
                    <Form.Item name="pool" label={t('brief.pool')} rules={[{required: true}]}>
                      <Select
                        style={{width: '100%'}}
                        showSearch
                        allowClear
                        labelInValue
                        placeholder={t('common.choose')}
                        optionFilterProp="children"
                        onSearch={debounce((value: string) => {
                          setPoolSearch(value)
                        }, 600)}
                        onChange={(data) => {
                          setSelectedPoolId(data ? data.value : null);
                          briefForm.setFieldValue('company', null);
                          briefForm.setFieldValue('products', null)
                        }}
                        filterOption={false}
                        options={poolsOptions}
                        disabled={briefId > 0}
                      />
                    </Form.Item>
                    <Form.Item name="company" label={t('brief.company')} rules={[{required: true}]}>
                      <Select
                        style={{width: '100%'}}
                        showSearch
                        allowClear
                        labelInValue
                        placeholder={t('common.choose')}
                        optionFilterProp="children"
                        onSearch={debounce((value: string) => {
                          setCompanySearch(value)
                        }, 600)}
                        onChange={(data) => {
                          setSelectedCompanyId(data ? data.value : null);
                          briefForm.setFieldValue('products', null)
                        }}
                        filterOption={false}
                        options={companiesOptions}
                        disabled={briefId > 0}
                      />
                    </Form.Item>
                    <Form.Item name="modelType" label={t('brief.modelType')} rules={[{required: true}]}>
                      <Select
                        style={{width: '100%'}}
                        allowClear
                        labelInValue
                        placeholder={t('common.choose')}
                        optionFilterProp="children"
                        onChange={(data) => {
                          if (data && modelTypes && modelTypes.data) {
                            const modelType = modelTypes.data.find((modelType: ModelType) =>
                              modelType["@id"] === data.value);
                            if (modelType) {
                              setSelectedModelType(modelType);

                              if (!modelType.isBriefManyProducts) {
                                briefForm.setFieldValue('products', null)
                              }
                            }
                          }
                        }}
                        filterOption={false}
                        options={modelTypesOptions}
                      />
                    </Form.Item>
                    <Form.Item name="products" label={t('brief.product')} rules={[{required: true}]}>
                      <Select
                        style={{width: '100%'}}
                        showSearch
                        allowClear
                        labelInValue
                        mode={isBriefManyProducts() ? "multiple" : undefined}
                        placeholder={t('common.choose')}
                        optionFilterProp="children"
                        onSearch={debounce((value: string) => {
                          setCompanySearch(value)
                        }, 600)}
                        filterOption={false}
                        options={productsOptions}
                      />
                    </Form.Item>
                    <Form.Item name="kpi" label={t('brief.kpi')} rules={[{required: true}]}>
                      <Select
                        style={{width: '100%'}}
                        allowClear
                        labelInValue
                        placeholder={t('common.choose')}
                        optionFilterProp="children"
                        filterOption={false}
                        options={kpiOptions}
                        onChange={(data) => {
                          if (kpis && kpis.data && data) {
                            const kpi = kpis.data.find((kpi: Kpi) => kpi["@id"] === data.value);

                            if (kpi && kpi.efficiencyMetrics && kpi.efficiencyMetrics.length > 0 && efficiencyMetrics && efficiencyMetrics.data) {
                              const kpiMetrics = efficiencyMetrics.data.filter((metric: Model) => kpi.efficiencyMetrics.includes(metric["@id"]));
                              const selectedMetrics = [];
                              for (let metric of kpiMetrics) {
                                selectedMetrics.push({
                                  disabled: undefined,
                                  key: metric['@id'],
                                  label: metric.name,
                                  title: undefined,
                                  value: metric['@id'],
                                })
                              }

                              briefForm.setFields([
                                {
                                  name: 'efficiencyMetrics',
                                  value: selectedMetrics,
                                  errors: []
                                }
                              ])
                            }
                          }
                        }}
                      />
                    </Form.Item>
                    <Form.Item name="kpiDescription" label={t('brief.kpiDescription')}>
                      <TextArea
                        placeholder={t('common.write')}
                        disabled={!Roles.hasAccess('canCreateOrUpdateBrief')}
                      />
                    </Form.Item>
                    <Form.Item name="efficiencyMetrics" label={t('brief.efficiencyMetrics')} rules={[{required: true}]}>
                      <Select
                        style={{width: '100%'}}
                        allowClear
                        labelInValue
                        mode="multiple"
                        placeholder={t('common.choose')}
                        optionFilterProp="children"
                        onSearch={debounce((value: string) => {
                          setEfficiencyMetricsSearch(value)
                        }, 600)}
                        filterOption={false}
                        options={efficiencyMetricsOptions}
                      />
                    </Form.Item>
                    <Form.Item name="specificText" label={t('brief.specificText')}>
                      <TextArea
                        placeholder={t('common.write')}
                        disabled={!Roles.hasAccess('canCreateOrUpdateBrief')}
                      />
                    </Form.Item>
                    <Form.Item name="mainCompetitors" label={t('brief.mainCompetitors')}>
                      <TextArea
                        placeholder={t('common.write')}
                        disabled={!Roles.hasAccess('canCreateOrUpdateBrief')}
                      />
                    </Form.Item>
                    <Form.Item name="seasonalityText" label={t('brief.seasonalityText')}>
                      <TextArea
                        placeholder={t('common.write')}
                        disabled={!Roles.hasAccess('canCreateOrUpdateBrief')}
                      />
                    </Form.Item>
                    <Form.Item name="bht" label={t('brief.bht')}>
                      <TextArea
                        placeholder={t('common.write')}
                        disabled={!Roles.hasAccess('canCreateOrUpdateBrief')}
                      />
                    </Form.Item>
                    <Form.Item name="audienceText" label={t('brief.audienceText')}>
                      <TextArea
                        placeholder={t('common.write')}
                        disabled={!Roles.hasAccess('canCreateOrUpdateBrief')}
                      />
                    </Form.Item>
                    <Form.Item name="period" label={t('brief.period')} rules={[{required: true}]}>
                      <RangePicker
                        format={'DD.MM.YYYY'}
                        maxDate={dayjs().add(-1, "day")}
                      />
                    </Form.Item>
                    <Form.Item
                      name="requiresValidation"
                      label={t('brief.requiresValidation')}
                      rules={[{required: true}]}
                    >
                      <Select options={[
                        {
                          value: true,
                          label: t('common.yes'),
                        },
                        {
                          value: false,
                          label: t('common.no'),
                        },
                      ]}/>
                    </Form.Item>
                    <Form.Item
                      name="isPresentationRequired"
                      label={t('brief.isPresentationRequired')}
                      rules={[{required: true}]}
                    >
                      <Select options={[
                        {
                          value: true,
                          label: t('common.yes'),
                        },
                        {
                          value: false,
                          label: t('common.no'),
                        },
                      ]}/>
                    </Form.Item>
                    <Form.Item
                      name="isCalculatorRequired"
                      label={t('brief.isCalculatorRequired')}
                      rules={[{required: true}]}
                    >
                      <Select options={[
                        {
                          value: true,
                          label: t('common.yes'),
                        },
                        {
                          value: false,
                          label: t('common.no'),
                        },
                      ]}/>
                    </Form.Item>
                  </div>
                </Form>
                <ButtonBlack
                  onClick={briefForm.submit}
                  disabled={!Roles.hasAccess('canCreateOrUpdateBrief') || (briefId > 0 && !isValuesChanged)}
                >{t('common.save')}</ButtonBlack>
              </Space>
            </Card>
          </Flex>
          <Flex style={{width: '640px', minWidth: '640px'}}>
            {briefId > 0 &&
              <Flex vertical gap={'small'}>
                <Card>
                  <span className={'criterion__stages-button__text'}>{t('edit.stages')}</span>
                  <Stages key={'tasks'}/>
                </Card>

                <WorkGroup/>
              </Flex>
            }
          </Flex>

        </Flex>
        : <Forbidden/>
      }
    </>

  )
}
